import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserCircle2 } from 'lucide-react';
import { submitPostal, whoami, isSignedIn } from '../../lib/api';
import { toast } from 'sonner';

export const PostalEntryBox = ({ competitionId }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    full_name: '', address_line1: '', address_line2: '', city: '', postcode: '',
    phone: '', skill_answer_index: 0, reference: '',
  });
  const [loading, setLoading] = useState(false);

  // Load account profile when signed in — postal name/address must match the account.
  useEffect(() => {
    if (!isSignedIn()) return;
    whoami().then((r) => {
      setUser(r.user);
      // Pre-fill (and lock) name + address from account.
      setForm((f) => ({
        ...f,
        full_name: r.user.full_name || '',
        phone: r.user.phone || '',
        address_line1: r.user.address_line1 || '',
        address_line2: r.user.address_line2 || '',
        city: r.user.city || '',
        postcode: r.user.postcode || '',
      }));
    }).catch(() => {
      localStorage.removeItem('pd_user_token');
      localStorage.removeItem('pd_user');
      setUser(null);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please sign in to lodge a postal entry.'); return; }
    setLoading(true);
    try {
      await submitPostal({ competition_id: competitionId, ...form });
      toast.success('Postal entry received. We will confirm by email once verified.');
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="comp-card border border-pd rounded-xl p-3 md:p-4" data-testid="postal-entry-box">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[9px] text-muted-sage uppercase mb-1 tracking-wide font-normal">No purchase necessary</div>
          <h3 className="text-base leading-tight font-normal text-muted-sage">Free postal entry route</h3>
          <p className="text-xs text-muted-sage mt-2 max-w-xl font-normal leading-relaxed">
            Every competition can be entered for free by post &mdash; same entry, same odds. Post a plain postcard with your name, address, phone,
            and your answer to the skill question to:{' '}
            <span className="font-normal">The Padel Draw Ltd, 5 Brayford Square, London E1 0SG</span>. Or lodge your intent
            below so we can match your postcard when it arrives.
          </p>
          <p className="text-[11px] text-muted-sage mt-2 font-normal leading-relaxed">
            Postal entries require a free account (your name, date of birth and address must match your account exactly). One envelope
            counts as one entry &mdash; post as many separate envelopes as you like, up to any per-competition cap. Proof of posting is not
            proof of entry or receipt. Full detail in clause 4 of our Terms.
          </p>
        </div>
        {user ? (
          <button
            onClick={() => setOpen((v) => !v)}
            className="shrink-0 text-xs text-muted-sage border border-pd rounded-lg px-2.5 py-1.5 font-normal hover:text-[color:var(--pd-text)] hover:border-[color:var(--pd-text)]/40"
            data-testid="postal-entry-toggle"
          >
            {open ? 'Hide form' : 'Lodge intent'}
          </button>
        ) : null}
      </div>

      {!user ? (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="postal-signin-gate">
          <Link
            to={`/register?returnTo=${encodeURIComponent(location.pathname)}`}
            className="inline-flex items-center border border-muted-sage/40 text-muted-sage hover:text-[color:var(--pd-text)] hover:border-[color:var(--pd-text)]/40 rounded-lg px-3 py-1.5 text-xs font-normal"
            data-testid="postal-cta-register"
          >
            Create free account
          </Link>
          <Link
            to={`/login?returnTo=${encodeURIComponent(location.pathname)}`}
            className="inline-flex items-center border border-muted-sage/40 text-muted-sage hover:text-[color:var(--pd-text)] hover:border-[color:var(--pd-text)]/40 rounded-lg px-3 py-1.5 text-xs font-normal"
            data-testid="postal-cta-signin"
          >
            Sign in
          </Link>
        </div>
      ) : null}

      {user && open ? (
        <form onSubmit={submit} className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2.5" data-testid="postal-entry-form">
          <div className="md:col-span-2 flex items-center gap-3 bg-[color:var(--pd-bg)] border border-pd rounded-xl px-3 py-2">
            <UserCircle2 size={16} className="text-muted-sage shrink-0" />
            <div className="min-w-0 text-[11px] font-normal">
              <div className="text-muted-sage truncate">{user.full_name} &middot; {user.email}</div>
              <div className="text-muted-sage/80 truncate">Details below are locked to your account &mdash; update your account to change them.</div>
            </div>
          </div>
          {[
            ['full_name', 'Full name', true],
            ['address_line1', 'Address line 1', true],
            ['address_line2', 'Address line 2 (optional)', true],
            ['city', 'City', true],
            ['postcode', 'Postcode', true],
            ['phone', 'Phone', true],
            ['reference', 'Reference (optional)', false],
          ].map(([k, l, locked]) => (
            <label key={k} className="text-[11px] font-mono text-muted-sage font-normal">
              {l}
              <input
                required={['full_name', 'address_line1', 'city', 'postcode'].includes(k)}
                readOnly={locked && k !== 'reference'}
                type="text"
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className={`mt-1 w-full h-10 bg-[color:var(--pd-bg)] border border-pd rounded-lg px-3 text-xs text-muted-sage font-normal ${locked && k !== 'reference' ? 'opacity-70 cursor-not-allowed' : 'focus:border-muted-sage'}`}
                data-testid={`postal-${k}`}
              />
            </label>
          ))}
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="border border-muted-sage/40 text-muted-sage hover:text-[color:var(--pd-text)] hover:border-[color:var(--pd-text)]/40 px-3 py-1.5 rounded-lg text-xs font-normal disabled:opacity-60"
              data-testid="postal-submit-button"
            >
              {loading ? 'Submitting\u2026' : 'Lodge intent'}
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
};

export default PostalEntryBox;
